<template>
  <section v-if="affichageMobile" class="mb-3">
    <!-- Template si tableau en cours de chargement -->
    <template v-if="busy === true || busy === 'true'">
      <div class="text-center text-warning my-3">
        <b-spinner class="align-middle"></b-spinner>
        <br />
        <strong>Chargement en cours...</strong>
      </div>
    </template>
    <!-- Template si pas de contenu à afficher -->
    <template v-else-if="(page ? page.content : []).length <= 0">
      <BubbleComponent>
        <slot name="empty">
          <p>
            <strong>Waouh !</strong>
          </p>
          <p v-if="false">
            <!-- FIXME Forte latence due au chargement de l'image -->
            <img style="max-width: 50vw; height: auto" src="/img/empty.gif" />
          </p>
          <p>
            <em>Il n'y a rien &agrave; afficher ici... ;-)</em>
            <br />
            <em>Pensez &agrave; v&eacute;rifier vos filtres !</em>
          </p>
        </slot>
      </BubbleComponent>
    </template>
    <!-- Template pour afficher le contenu mobile -->
    <template v-else>
      <template v-for="item in page ? page.content : []">
        <slot name="mobile-card" v-bind="{ item }"></slot>
      </template>
    </template>
    <section class="mt-3" style="position: sticky; bottom: 0">
      <MobilePaginationBar
        :page="page"
        @perPageChanged="perPageChanged"
        @pageChanged="pageChanged"
      ></MobilePaginationBar>
    </section>
  </section>
  <section
    v-else
    class="mb-3"
    @keydown.ctrl.up="() => $refs['paginationFooter'].first()"
    @keydown.ctrl.left="() => $refs['paginationFooter'].previous()"
    @keydown.ctrl.right="() => $refs['paginationFooter'].next()"
    @keydown.ctrl.down="() => $refs['paginationFooter'].last()"
  >
    <b-table
      :id="id"
      table-class="paginated-table mb-0"
      thead-class="sticky-top"
      head-variant="light"
      hover
      :tbody-tr-class="tbodyTrClass"
      :stacked="stacked"
      :busy="busy"
      sort-icon-left
      no-local-sorting
      show-empty
      :fields="computedFields"
      :items="page ? page.content : []"
      :sort-by="computedSortBy"
      :sort-desc="computedSortDesc"
      @sort-changed="sortChanged"
    >
      <!-- Template si tableau en cours de chargement -->
      <template #table-busy>
        <div class="text-center text-warning my-3">
          <b-spinner class="align-middle"></b-spinner>
          <br />
          <strong>Chargement en cours...</strong>
        </div>
      </template>
      <!-- Template si pas de contenu à afficher -->
      <template #empty>
        <BubbleComponent>
          <slot name="empty">
            <p>
              <strong>Waouh !</strong>
            </p>
            <p v-if="false">
              <!-- FIXME Forte latence due au chargement de l'image -->
              <img src="/img/empty.gif" />
            </p>
            <p>
              <em>Il n'y a rien &agrave; afficher ici... ;-)</em>
              <br />
              <em>Pensez &agrave; v&eacute;rifier vos filtres !</em>
            </p>
          </slot>
        </BubbleComponent>
      </template>
      <!-- Templates des colonnes pour afficher le contenu -->
      <template v-for="slotName in tableSlots" #[slotName]="context">
        <slot :name="slotName" v-bind="context"></slot>
      </template>
      <template #custom-foot="{ columns }">
        <b-tr>
          <b-td :colspan="columns" class="p-0 border-0 pagination-cell">
            <DesktopPaginationBar
              ref="paginationFooter"
              :label="label"
              :page="page"
              @perPageChanged="perPageChanged"
              @pageChanged="pageChanged"
            >
              <slot name="pagination"></slot>
            </DesktopPaginationBar>
          </b-td>
        </b-tr>
      </template>
    </b-table>
  </section>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import MobilePaginationBar from "./MobilePaginationBar.vue";
import BubbleComponent from "./BubbleComponent.vue";
import DesktopPaginationBar from "./DesktopPaginationBar.vue";
import UtilsService from "../services/utils.service";
export default {
  name: "PaginatedTable",
  components: { MobilePaginationBar, BubbleComponent, DesktopPaginationBar },
  /**
   * fields = paramétrage des champs de la table
   * tbodyTrClass = valeur de la propriété tbody-tr-class de la table
   * stacked = valeur de la propriété stacked de la table
   * busy = valeur de la propriété busy de la table
   * pageable = valeur initiale du pageable
   * page = résultat paginé
   * label = nom de l'objet (ex : parcelle(s))
   */
  props: [
    "fields",
    "tbodyTrClass",
    "stacked",
    "busy",
    "pageable",
    "page",
    "label",
  ],
  data() {
    return {
      id: `table-${uuidv4()}`,
      // Slots réservés (ne peuvent pas être utilisés comme fields)
      specialSlots: [
        "mobile-card",
        "table-busy",
        "empty",
        "custom-foot",
        "pagination",
      ],
    };
  },
  computed: {
    affichageMobile() {
      return (
        this.$store.getters["affichageMobile"] &&
        Object.keys(this.$scopedSlots).find((name) => name === "mobile-card")
      );
    },
    computedFields() {
      return (UtilsService.deepCopy(this.fields) || []).map((f) => {
        // Centrage vertical systématique des libellés de colonnes
        if (!f.thClass) {
          f.thClass = "align-middle";
        } else if (typeof f.thClass === "string") {
          f.thClass = `${f.thClass} align-middle`;
        } else if (Array.isArray(f.thClass)) {
          f.thClass.push("align-middle");
        }

        // Alignement horizontal du contenu
        if (f.alignH) {
          f.thClass = `${f.thClass ?? ""} text-${f.alignH}`;
          f.tdClass = `${f.tdClass ?? ""} text-${f.alignH}`;
        }

        // Comportement responsive
        if (f.breakpoint) {
          f.thClass = `${f.thClass ?? ""} d-none d-${f.breakpoint}-table-cell`;
          f.tdClass = `${f.tdClass ?? ""} d-none d-${f.breakpoint}-table-cell`;
        }

        return f;
      });
    },
    computedSortBy() {
      // Si le pageable en entrée implémente le tri multiple
      // Alors on ne garde que la 1ère propriété (b-table ne le gère pas)
      return Array.isArray(this.pageable.sortBy)
        ? this.pageable.sortBy[0]
        : this.pageable.sortBy;
    },
    computedSortDesc() {
      // Si le pageable en entrée implémente le tri multiple
      // Alors on ne garde que la 1ère propriété (b-table ne le gère pas)
      return Array.isArray(this.pageable.sortDesc)
        ? this.pageable.sortDesc[0] ?? false
        : this.pageable.sortDesc;
    },
    tableSlots() {
      return Object.keys(this.$scopedSlots).filter(
        (name) => !this.specialSlots.includes(name)
      );
    },
  },
  methods: {
    sortChanged(tableContext) {
      if (tableContext.sortBy) {
        let pageable = UtilsService.deepCopy(this.pageable);
        // Si le pageable en entrée implémente le tri multiple
        // Alors on renvoie les propriétés de tri sous forme de tableau
        pageable.sortBy = Array.isArray(this.pageable.sortBy)
          ? [tableContext.sortBy]
          : tableContext.sortBy;
        pageable.sortDesc = Array.isArray(this.pageable.sortDesc)
          ? [tableContext.sortDesc]
          : tableContext.sortDesc;
        this.$emit("change", pageable);
      }
    },
    perPageChanged(perPage) {
      let pageable = UtilsService.deepCopy(this.pageable);
      pageable.perPage = perPage;
      this.$emit("change", pageable);
    },
    pageChanged(numPage) {
      let pageable = UtilsService.deepCopy(this.pageable);
      pageable.currentPage = numPage;
      this.$emit("change", pageable);
    },
  },
};
</script>
